<template>
  <v-dialog v-model="openMode" max-width="480px" content-class="delete-dialog-wrapper" persistent>
    <v-card class="delete-card">
      <v-card-title class="delete-header">
        <div class="header-content">
          <v-avatar size="48" color="#ffebee" class="warning-avatar">
            <v-icon size="28" color="#f44336">mdi-alert-circle-outline</v-icon>
          </v-avatar>
          <div class="header-text">
            <h2 class="header-title">Delete Confirmation</h2>
            <p class="header-subtitle">This action cannot be undone</p>
          </div>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="delete-content">
        <div class="warning-section">
          <div class="warning-icon-container">
            <v-icon size="64" color="#ff9800" class="warning-icon">mdi-delete-alert</v-icon>
          </div>

          <div class="warning-text">
            <h3 class="warning-title">
              {{ isBulkDelete ? `Are you sure you want to delete ${items.length} items?` : 'Are you sure you want to delete this item?' }}
            </h3>
            <p v-if="actionType === 'hardDelete'" class="warning-description">
              {{ isBulkDelete ? 'Items will be permanently deleted from the system and cannot be restored.' : 'The item will be permanently deleted from the system and cannot be restored.' }}
              This action will affect all data related {{ isBulkDelete ? 'to these items' : 'to this item' }}.
            </p>
          </div>
        </div>

        <div v-if="!isBulkDelete && item && (item.caller_name || item.integer_id)" class="item-details">
          <v-card class="details-card" outlined>
            <v-card-text class="details-content">
              <div class="detail-row" v-if="item.integer_id">
                <span class="detail-label">File Number:</span>
                <span class="detail-value">#{{ item.integer_id }}</span>
              </div>
              <div class="detail-row" v-if="item.caller_name">
                <span class="detail-label">Name:</span>
                <span class="detail-value">{{ item.caller_name }}</span>
              </div>
              <div class="detail-row" v-if="item.record_duration">
                <span class="detail-label">Duration:</span>
                <span class="detail-value">{{ item.record_duration }}</span>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <div v-if="isBulkDelete && items && items.length > 0" class="item-details">
          <v-card class="details-card" outlined>
            <v-card-text class="details-content">
              <div class="bulk-summary">
                <div class="detail-row">
                  <span class="detail-label">Total Items:</span>
                  <span class="detail-value">{{ items.length }}</span>
                </div>
                <div class="bulk-items-list" v-if="items.length <= 5">
                  <div class="bulk-item" v-for="(bulkItem, index) in items" :key="index">
                    <v-icon small color="#0d2c6d">mdi-file-music</v-icon>
                    <span>{{ bulkItem.caller_name || 'No Name' }} (#{{ bulkItem.integer_id }})</span>
                  </div>
                </div>
                <div v-else class="bulk-items-preview">
                  <p class="preview-text">Including: {{ items.slice(0, 3).map(i => i.caller_name || 'No Name').join(', ') }} and {{ items.length - 3 }} more...</p>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <div v-if="progressShow" class="loading-section">
          <v-progress-circular indeterminate color="#0d2c6d" size="40" width="4"
            class="loading-spinner"></v-progress-circular>
          <p class="loading-text">{{ isBulkDelete ? 'Deleting items...' : 'Deleting item...' }}</p>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="delete-actions">
        <div class="actions-content">
          <v-btn text class="cancel-btn" :disabled="progressShow" @click="closeDelete">
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn color="#f44336" class="delete-btn" :disabled="progressShow" :loading="progressShow"
            @click="actionConfirmed" elevation="2">
            <v-icon left>mdi-delete-forever</v-icon>
            {{ actionType === 'hardDelete' ? 'Delete Permanently' : 'Delete' }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
  </v-dialog>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'

export default {
  props: {
    item: Object,
    items: { type: Array, default: () => [] }, //  Added items prop for bulk delete
    isBulkDelete: { type: Boolean, default: false }, //  Added isBulkDelete prop
    actionType: String,
    value: { type: Boolean, default: false },
  },
  components: {
    SnackBar,
  },
  data: () => ({
    progressShow: false,
    snackbar: false,
    snackbarColorBt: "green",
    snacbarText: "",
  }),
  methods: {
    actionConfirmed() {
      //  Call appropriate delete method based on isBulkDelete
      if (this.isBulkDelete) {
        this.deleteMultipleItems();
      } else {
        this.deleteItem();
      }
    },

    async deleteItem() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        const itemsJSON = JSON.stringify({ item: this.item, actionType: this.actionType });

        let api = process.env.VUE_APP_BASE_URL + "/records/delete_record";
        const res = await fetch(api, ApiServices.requestOptions("DELETE", itemsJSON, token));

        if (res.status >= 400) {
          this.$emit("itemDeleted", "", "Error deleting recording", "red");
        } else if (res.status === 200) {
          this.$emit("itemDeleted", this.item._id, "Recording deleted successfully!", "green");
          this.openMode = false;
        }
        this.progressShow = false;
      } catch (error) {
        this.progressShow = false;
        this.$emit("itemDeleted", "", "Error deleting recording", "red");
      }
    },

    //  New method for bulk delete
    async deleteMultipleItems() {
      try {
        this.progressShow = true;
        let token = localStorage.getItem("token");
        const itemsJSON = JSON.stringify({ items: this.items, actionType: this.actionType });

        let api = process.env.VUE_APP_BASE_URL + "/records/delete_multiple_records";
        const res = await fetch(api, ApiServices.requestOptions("DELETE", itemsJSON, token));

        if (res.status >= 400) {
          this.$emit("itemsDeleted", [], "Error deleting recordings", "red");
        } else if (res.status === 200) {
          const deletedIds = this.items.map(item => item._id);
          this.$emit("itemsDeleted", deletedIds, `${this.items.length} recordings deleted successfully!`, "green");
          this.openMode = false;
        }
        this.progressShow = false;
      } catch (error) {
        this.progressShow = false;
        this.$emit("itemsDeleted", [], "Error deleting recordings", "red");
      }
    },

    closeDelete() {
      this.openMode = false;
    }
  },
  computed: {
    openMode: {
      get() {
        return this.value
      },
      set(v) {
        return this.$emit('input', v)
      }
    },
  },
}
</script>

<style scoped>
/* Keeping styles exactly as they were */
.delete-dialog-wrapper {
  border-radius: 16px;
  overflow: hidden;
}

.delete-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(244, 67, 54, 0.15);
}

.delete-header {
  padding: 24px;
  background: linear-gradient(135deg, #fef6f6 0%, #ffebee 100%);
  border-bottom: 1px solid rgba(244, 67, 54, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.warning-avatar {
  flex-shrink: 0;
}

.header-text {
  flex: 1;
}

.header-title {
  color: #d32f2f;
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
}

.header-subtitle {
  color: rgba(244, 67, 54, 0.7);
  font-size: 0.875rem;
  margin: 4px 0 0 0;
}

.delete-content {
  padding: 24px;
}

.warning-section {
  text-align: center;
  margin-bottom: 24px;
}

.warning-icon-container {
  margin-bottom: 16px;
}

.warning-icon {
  opacity: 0.8;
}

.warning-title {
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 12px;
  line-height: 1.3;
}

.warning-description {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
  max-width: 400px;
  margin: 0 auto;
}

.item-details {
  margin-bottom: 24px;
}

.details-card {
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 12px;
  background: rgba(244, 67, 54, 0.02);
}

.details-content {
  padding: 16px !important;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  font-size: 0.9rem;
}

.detail-value {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

/* Added styles for bulk delete items display */
.bulk-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bulk-items-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.bulk-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: rgba(13, 44, 109, 0.05);
  border-radius: 8px;
  font-size: 0.9rem;
}

.bulk-items-preview {
  margin-top: 8px;
  padding: 12px;
  background: rgba(13, 44, 109, 0.05);
  border-radius: 8px;
}

.preview-text {
  margin: 0;
  font-size: 0.9rem;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.4;
}

.loading-section {
  text-align: center;
  padding: 20px 0;
}

.loading-spinner {
  margin-bottom: 16px;
}

.loading-text {
  color: #0d2c6d;
  font-weight: 500;
  margin: 0;
}

.delete-actions {
  padding: 20px 24px;
  background: #fafbfc;
}

.actions-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.cancel-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0;
  color: rgba(0, 0, 0, 0.6);
  padding: 0 20px;
  height: 40px;
  border-radius: 10px;
}

.cancel-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.delete-btn {
  background: linear-gradient(135deg, #f44336, #d32f2f) !important;
  color: white !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  padding: 0 24px;
  height: 40px;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.delete-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(244, 67, 54, 0.3) !important;
}

.delete-btn:disabled {
  background: rgba(0, 0, 0, 0.12) !important;
  color: rgba(0, 0, 0, 0.38) !important;
}

/* Responsive */
@media (max-width: 600px) {
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .actions-content {
    flex-direction: column;
    gap: 12px;
  }

  .cancel-btn,
  .delete-btn {
    width: 100%;
  }
}
</style>